import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import Layout from '../Layout';
import { getData } from '../../helpers/ApiSettings';
import { CartManager } from '../../helpers/cart';
import { showToast } from '../../helpers/customToast';
import QuantitySelector from '../QuantitySelector';

const PreOrderPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [preOrder, setPreOrder] = useState(null);
    const [selectedItems, setSelectedItems] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPreOrder = async () => {
            try {
                setLoading(true);
                const response = await getData(`/pre-orders/${id}?populate[pre_order_items][populate][product][populate]=images`);
                setPreOrder(response.data.data);
                // console.log(response);
                
                // Initialize selected quantities
                const initialQuantities = {};
                response.data.data.pre_order_items.forEach(item => {
                    initialQuantities[item.id] = 1;
                });
                setSelectedItems(initialQuantities);
                
                setError(null);
            } catch (err) {
                setError('Erreur lors du chargement de la pré-commande');
                console.error('Error fetching pre-order:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPreOrder();
    }, [id]);

    const handleQuantityChange = (itemId, quantity) => {
        setSelectedItems(prev => ({
            ...prev,
            [itemId]: quantity
        }));
    };

    const calculateTotalPrice = () => {
        if (!preOrder) return 0;

        return Object.entries(selectedItems).reduce((total, [itemId, quantity]) => {
            const item = preOrder.pre_order_items.find(i => i.id === parseInt(itemId));
            return total + (item ? item.price * quantity : 0);
        }, 0);
    };

    const handleAddToCart = () => {
        // Check if any items are selected
        const hasSelectedItems = Object.values(selectedItems).some(quantity => quantity > 0);
        if (!hasSelectedItems) {
            showToast('Veuillez sélectionner au moins un article.', 'error');
            return;
        }

        // Create cart items for each selected pre-order item
        Object.entries(selectedItems).forEach(([itemId, quantity]) => {
            if (quantity > 0) {
                const item = preOrder.pre_order_items.find(i => i.id === parseInt(itemId));
                if (item) {
                    const cartItem = {
                        id: item.product.id,
                        preOrderItemId: item.id,
                        price: item.price,
                        startDate: new Date(item.start_date).toISOString(),
                        endDate: new Date(item.end_date).toISOString(),
                        quantity: quantity,
                        product: item.product,
                        isPreOrder: true,
                        preOrderId: preOrder.id,
                        // deposit: preOrder.deposit
                    };

                    if (CartManager.addToCart(cartItem)) {
                        showToast('Article ajouté au panier !', 'success');
                    } else {
                        showToast('Cet article est déjà dans votre panier.', 'warn');
                    }
                }
            }
        });

        // Navigate to cart page
        navigate('/panier');
    };

    if (loading) {
        return (
            <Layout>
                <div className="container mt-5">
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="container mt-5">
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                </div>
            </Layout>
        );
    }

    if (!preOrder) {
        return (
            <Layout>
                <div className="container mt-5">
                    <div className="alert alert-warning" role="alert">
                        Pré-commande non trouvée
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container mt-5 mb-5">
                <h1 className="mb-4">Pré-commande #{preOrder.id}</h1>

                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Produit</th>
                                        <th>Période</th>
                                        <th>Prix unitaire</th>
                                        <th>Quantité</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preOrder.pre_order_items.map((item) => (
                                        <tr key={item.id}>
                                            <td>
                                                <h5 className="mb-0">{item.product.titre}</h5>
                                                <small className="text-muted">Réf : {item.product.reference}</small>
                                            </td>
                                            <td>
                                                {new Date(item.start_date).toLocaleDateString('fr-FR')} 
                                                {' au '} 
                                                {new Date(item.end_date).toLocaleDateString('fr-FR')}
                                            </td>
                                            <td>
                                                {new Intl.NumberFormat('fr-FR', {
                                                    style: 'currency',
                                                    currency: 'EUR'
                                                }).format(item.price)}
                                            </td>
                                            <td style={{ width: '150px' }}>
                                                <QuantitySelector
                                                    value={selectedItems[item.id]}
                                                    onChange={(quantity) => handleQuantityChange(item.id, quantity)}
                                                    maxQuantity={item.product.quantity}
                                                />
                                            </td>
                                            <td>
                                                {new Intl.NumberFormat('fr-FR', {
                                                    style: 'currency',
                                                    currency: 'EUR'
                                                }).format(item.price * selectedItems[item.id])}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="4" className="text-end"><strong>Total :</strong></td>
                                        <td>
                                            <strong>
                                                {new Intl.NumberFormat('fr-FR', {
                                                    style: 'currency',
                                                    currency: 'EUR'
                                                }).format(calculateTotalPrice())}
                                            </strong>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="d-flex justify-content-end mt-4">
                            <button 
                                className="btn btn-primary"
                                onClick={handleAddToCart}
                            >
                                <ShoppingCart className="me-2" />
                                Ajouter au panier
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default PreOrderPage;